import * as React from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import moment from "moment";
import toast from "react-hot-toast";
import secureLocalStorage from "react-secure-storage";
import API from "../Api/api";
import { colors } from "../brand";
import { reportLabel } from "../utils/reportName";

const BASE = "/api/v1/reconciliation-deletion-requests";

const money = (v) =>
  Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const day = (d) => (d ? moment(d).format("D MMM YYYY") : "—");
const errorText = (e, fallback) => e?.response?.data?.message || fallback;

const STATUS = {
  pending: { label: "Waiting", bg: colors.warningSoft, color: colors.warning },
  deleted: { label: "Deleted", bg: colors.dangerSoft, color: colors.danger },
  rejected: { label: "Rejected", bg: "#eef2f6", color: colors.textSecondary },
  withdrawn: { label: "Withdrawn", bg: "#eef2f6", color: colors.textSecondary },
};

const headCell = {
  fontWeight: 700,
  fontSize: 12,
  color: colors.textSecondary,
  textTransform: "uppercase",
  letterSpacing: 0.3,
  backgroundColor: colors.surfaceSoft,
  whiteSpace: "nowrap",
};

function Empty({ children }) {
  return (
    <TableRow>
      <TableCell colSpan={8} sx={{ py: 4, textAlign: "center", color: colors.textSecondary }}>
        {children}
      </TableCell>
    </TableRow>
  );
}

function LoadingRows({ cols }) {
  return [0, 1, 2].map((i) => (
    <TableRow key={i}>
      {Array.from({ length: cols }).map((_, j) => (
        <TableCell key={j}>
          <Skeleton />
        </TableCell>
      ))}
    </TableRow>
  ));
}

export default function DeletionRequests() {
  const user = React.useMemo(() => {
    try {
      return JSON.parse(secureLocalStorage.getItem("profile"))?.results || {};
    } catch {
      return {};
    }
  }, []);
  const role = user.reconciliation;
  const isAdmin = role === "Administrator";
  const canRequest = ["Read-Write", "Approver", "Administrator"].includes(role);

  const locations = useSelector((state) => state.location) || [];
  const locationNames = locations.map((l) => l.name);
  const label = (r) => reportLabel(r, locationNames);

  const [tab, setTab] = React.useState("waiting");
  const [requests, setRequests] = React.useState([]);
  const [reports, setReports] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");

  // Dialog state: { kind: "request" | "delete" | "reject", item }
  const [dialog, setDialog] = React.useState(null);
  const [reason, setReason] = React.useState("");
  const [confirmText, setConfirmText] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [reqRes, repRes] = await Promise.all([
        API.get(BASE),
        API.get("/api/v1/reconciliation-submission?approved=true"),
      ]);
      setRequests(reqRes.data?.results || []);
      setReports(Array.isArray(repRes.data) ? repRes.data : []);
    } catch (e) {
      toast.error(errorText(e, "Could not load deletion requests"));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const pending = requests.filter((r) => r.status === "pending");
  const history = requests.filter((r) => r.status !== "pending");
  const pendingFor = new Set(pending.map((r) => String(r.submission)));

  const q = search.trim().toLowerCase();
  const visibleReports = reports
    .filter(
      (r) =>
        !q ||
        label(r).toLowerCase().includes(q) ||
        day(r.submission_date).toLowerCase().includes(q) ||
        String(r.prepared_by || "").toLowerCase().includes(q),
    )
    .sort((a, b) => new Date(b.submission_date) - new Date(a.submission_date));

  const openDialog = (kind, item) => {
    setReason("");
    setConfirmText("");
    setDialog({ kind, item });
  };
  const closeDialog = () => !busy && setDialog(null);

  const submit = async () => {
    const { kind, item } = dialog;
    setBusy(true);
    try {
      if (kind === "request") {
        await API.post(BASE, { submissionId: item._id, reason });
        toast.success("Deletion requested. An Administrator will review it.");
        setTab("waiting");
      } else if (kind === "delete") {
        const { data } = await API.post(`${BASE}/${item._id}/approve`, {
          note: reason,
        });
        const n = (data?.removed?.credits || 0) + (data?.removed?.debits || 0);
        toast.success(`Report deleted with its ${n} outstanding item${n === 1 ? "" : "s"}`);
      } else if (kind === "reject") {
        await API.post(`${BASE}/${item._id}/reject`, { note: reason });
        toast.success("Request rejected; the report is kept");
      }
      setDialog(null);
      await load();
    } catch (e) {
      toast.error(errorText(e, "That did not work, please try again"));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (item) => {
    try {
      await API.post(`${BASE}/${item._id}/withdraw`);
      toast.success("Request withdrawn");
      await load();
    } catch (e) {
      toast.error(errorText(e, "Could not withdraw the request"));
    }
  };

  const mine = (r) => String(r.requested_by?.id) === String(user._id);

  const dialogReady =
    dialog?.kind === "request"
      ? reason.trim().length >= 10
      : dialog?.kind === "delete"
        ? confirmText === "DELETE"
        : true;

  return (
    <Box>
      <Helmet>
        <title>Delete Reports | Reconciliation</title>
      </Helmet>

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: colors.textPrimary }}>
          Delete approved reports
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: colors.textSecondary }}>
          One person requests the deletion with a reason; an Administrator who
          did not ask deletes it or rejects the request.
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "14px",
          border: `1px solid ${colors.border}`,
          overflow: "hidden",
        }}
      >
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="scrollable"
          sx={{
            px: 1,
            borderBottom: `1px solid ${colors.border}`,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 700 },
          }}
        >
          <Tab value="waiting" label={`Waiting (${pending.length})`} />
          {canRequest && <Tab value="request" label="Request a deletion" />}
          <Tab value="history" label="History" />
        </Tabs>

        {tab === "waiting" && (
          <TableContainer sx={{ maxHeight: "calc(100dvh - 250px)" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCell}>Report</TableCell>
                  <TableCell sx={headCell}>As at</TableCell>
                  <TableCell sx={headCell}>Reason</TableCell>
                  <TableCell sx={headCell}>Requested by</TableCell>
                  <TableCell sx={headCell} align="right">
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <LoadingRows cols={5} />
                ) : pending.length === 0 ? (
                  <Empty>No deletion requests are waiting.</Empty>
                ) : (
                  pending.map((r) => (
                    <TableRow key={r._id} hover>
                      <TableCell sx={{ fontWeight: 700, textTransform: "capitalize" }}>
                        {label(r)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {day(r.submission_date)}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>{r.reason}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {r.requested_by?.name || "—"}
                        <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
                          {moment(r.createdAt).fromNow()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {mine(r) ? (
                          <Button
                            size="small"
                            onClick={() => withdraw(r)}
                            sx={{ textTransform: "none", fontWeight: 700 }}
                          >
                            Withdraw
                          </Button>
                        ) : isAdmin ? (
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              onClick={() => openDialog("reject", r)}
                              sx={{ textTransform: "none", fontWeight: 700, color: colors.textSecondary }}
                            >
                              Reject
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              disableElevation
                              startIcon={<DeleteForeverOutlinedIcon />}
                              onClick={() => openDialog("delete", r)}
                              sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}
                            >
                              Delete report
                            </Button>
                          </Stack>
                        ) : (
                          <Typography sx={{ fontSize: 12.5, color: colors.textSecondary }}>
                            Waiting for an Administrator
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {tab === "request" && canRequest && (
          <>
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${colors.border}` }}>
              <TextField
                size="small"
                placeholder="Search by location, date or preparer"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ width: { xs: "100%", sm: 340 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <TableContainer sx={{ maxHeight: "calc(100dvh - 310px)" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCell}>Approved report</TableCell>
                    <TableCell sx={headCell}>As at</TableCell>
                    <TableCell sx={headCell}>Prepared by</TableCell>
                    <TableCell sx={headCell}>Approved by</TableCell>
                    <TableCell sx={headCell} align="right">
                      Our balance
                    </TableCell>
                    <TableCell sx={headCell} align="right">
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <LoadingRows cols={6} />
                  ) : visibleReports.length === 0 ? (
                    <Empty>
                      {q ? "No approved report matches your search." : "There are no approved reports."}
                    </Empty>
                  ) : (
                    visibleReports.map((r) => {
                      const requested = pendingFor.has(String(r._id));
                      return (
                        <TableRow key={r._id} hover>
                          <TableCell sx={{ fontWeight: 700, textTransform: "capitalize" }}>
                            {label(r)}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {day(r.submission_date)}
                          </TableCell>
                          <TableCell>{r.prepared_by || "—"}</TableCell>
                          <TableCell>{r.approved_by || "—"}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            {money(r.primary_balance)}
                          </TableCell>
                          <TableCell align="right">
                            {requested ? (
                              <Chip
                                size="small"
                                label="Requested"
                                sx={{ fontWeight: 700, backgroundColor: colors.warningSoft }}
                              />
                            ) : (
                              <Button
                                size="small"
                                color="error"
                                onClick={() => openDialog("request", r)}
                                sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
                              >
                                Request deletion
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {tab === "history" && (
          <TableContainer sx={{ maxHeight: "calc(100dvh - 250px)" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCell}>Report</TableCell>
                  <TableCell sx={headCell}>As at</TableCell>
                  <TableCell sx={headCell}>Outcome</TableCell>
                  <TableCell sx={headCell}>Reason</TableCell>
                  <TableCell sx={headCell}>Requested by</TableCell>
                  <TableCell sx={headCell}>Decided by</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <LoadingRows cols={6} />
                ) : history.length === 0 ? (
                  <Empty>No decisions yet.</Empty>
                ) : (
                  history.map((r) => {
                    const s = STATUS[r.status] || STATUS.rejected;
                    return (
                      <TableRow key={r._id} hover>
                        <TableCell sx={{ fontWeight: 700, textTransform: "capitalize" }}>
                          {label(r)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {day(r.submission_date)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={s.label}
                            sx={{ fontWeight: 700, backgroundColor: s.bg, color: s.color }}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>
                          {r.reason}
                          {r.decision_note && (
                            <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
                              Note: {r.decision_note}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {r.requested_by?.name || "—"}
                          <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
                            {day(r.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {r.decided_by?.name || "—"}
                          <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
                            {day(r.decided_at)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={Boolean(dialog)}
        onClose={closeDialog}
        PaperProps={{ sx: { borderRadius: "16px", width: 480 } }}
      >
        {dialog && (
          <>
            <DialogTitle sx={{ fontWeight: 800 }}>
              {dialog.kind === "request"
                ? "Request deletion"
                : dialog.kind === "delete"
                  ? "Delete this report?"
                  : "Reject this request?"}
            </DialogTitle>
            <DialogContent>
              <Box
                sx={{
                  p: 1.5,
                  mb: 2,
                  borderRadius: "12px",
                  background: colors.surfaceSoft,
                  border: `1px solid ${colors.border}`,
                }}
              >
                <Typography sx={{ fontWeight: 700, textTransform: "capitalize" }}>
                  {label(dialog.item)}
                </Typography>
                <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
                  As at {day(dialog.item.submission_date)} · prepared by{" "}
                  {dialog.item.prepared_by || "—"} · approved by{" "}
                  {dialog.item.approved_by || "—"}
                </Typography>
                {dialog.kind !== "request" && (
                  <Typography sx={{ fontSize: 13, mt: 1 }}>
                    <strong>{dialog.item.requested_by?.name}</strong> asked
                    because: {dialog.item.reason}
                  </Typography>
                )}
              </Box>

              {dialog.kind === "delete" && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>
                  The report and all its outstanding items are removed for
                  good. The next reconciliation for these locations will carry
                  forward from the report before this one.
                </Alert>
              )}

              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={dialog.kind === "request" ? 3 : 2}
                label={
                  dialog.kind === "request"
                    ? "Why should it be deleted?"
                    : "Note (optional)"
                }
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                helperText={
                  dialog.kind === "request"
                    ? reason.trim().length < 10
                      ? "At least 10 characters"
                      : "An Administrator will see this"
                    : " "
                }
              />

              {dialog.kind === "delete" && (
                <TextField
                  fullWidth
                  sx={{ mt: 1 }}
                  label='Type "DELETE" to confirm'
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                />
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button
                onClick={closeDialog}
                disabled={busy}
                sx={{ textTransform: "none", color: colors.textSecondary }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disableElevation
                disabled={busy || !dialogReady}
                color={dialog.kind === "reject" ? "primary" : "error"}
                onClick={submit}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}
              >
                {busy
                  ? "Working…"
                  : dialog.kind === "request"
                    ? "Send request"
                    : dialog.kind === "delete"
                      ? "Delete report"
                      : "Reject request"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
